import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaLinkedin } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: 'Site Vitrine',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Message envoyé avec succès ! Nous vous répondrons sous 24h.'
        });
        setFormData({
          name: '',
          email: '',
          service: 'Site Vitrine',
          message: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Une erreur est survenue. Veuillez réessayer.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Impossible de se connecter au serveur. Contactez-nous par téléphone au 06 89 17 73 85.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      label: 'Téléphone',
      value: '06 89 17 73 85',
      href: 'tel:+33689177385',
      color: 'primary'
    },
    {
      icon: <FaEnvelope />,
      label: 'Email',
      value: 'contact@olney.dev',
      href: 'mailto:contact@olney.dev',
      color: 'secondary'
    },
    {
      icon: <FaWhatsapp />,
      label: 'WhatsApp',
      value: 'Envoyer un message',
      href: 'https://wa.me/33689177385',
      color: 'success'
    },
    {
      icon: <FaMapMarkerAlt />,
      label: 'Localisation',
      value: 'Roncq, Hauts-de-France',
      href: null,
      color: 'accent'
    }
  ];

  const services = [
    'Site Vitrine',
    'E-commerce',
    'Application Web',
    'Application Mobile',
    'SEO / Référencement',
    'Maintenance',
    'Cybersécurité',
    'Automatisation / IA',
    'Autre'
  ];


  return (
    <>
      <Helmet>
        <title>Contact | Devis Gratuit en 24h | Olney Tech Solutions</title>
        <meta name="description" content="Contactez Olney Tech Solutions pour votre projet web. Devis gratuit en 24h, rendez-vous téléphonique ou en visio. Agence web à Lille, Roncq, Phalempin." />
        <link rel="canonical" href="https://olney.dev/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)] text-sm font-bold tracking-widest uppercase">
              Contact
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
              Parlons de votre <span className="text-gradient-electric">projet</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Réponse garantie sous 24h. Premier rendez-vous gratuit et sans engagement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-bold text-white mb-8 font-['Space_Grotesk']">
                Restons en contact
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="flex items-center gap-4 group"
                      >
                        <div className={`w-14 h-14 rounded-2xl bg-[var(--${item.color})]/10 flex items-center justify-center text-[var(--${item.color})] group-hover:scale-110 group-hover:bg-[var(--${item.color})] group-hover:text-white transition-all duration-300`}>
                          {item.icon}
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm uppercase tracking-wider font-bold mb-1">{item.label}</div>
                          <div className={`text-white text-lg font-bold group-hover:text-[var(--${item.color})] transition-colors`}>
                            {item.value}
                          </div>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-[var(--${item.color})]/10 flex items-center justify-center text-[var(--${item.color})]`}>
                          {item.icon}
                        </div>
                        <div>
                          <div className="text-gray-400 text-sm uppercase tracking-wider font-bold mb-1">{item.label}</div>
                          <div className="text-white text-lg font-bold">{item.value}</div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Availability */}
              <div className="glass-panel rounded-2xl p-6 border-[var(--primary)]/30">
                <div className="flex items-center gap-3 mb-4">
                  <FaClock className="text-[var(--primary)]" />
                  <span className="text-white font-bold">Disponibilité</span>
                </div>
                <div className="space-y-2 text-gray-400 text-sm">
                  <p>Lundi - Vendredi : 9h - 19h</p>
                  <p>Samedi : 10h - 17h (sur RDV)</p>
                  <p className="text-[var(--success)]">
                    Actuellement disponible pour nouveaux projets
                  </p>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <p className="text-gray-400 text-sm mb-4">Retrouvez-nous sur</p>
                <div className="flex gap-4">
                  <a
                    href="https://linkedin.com/company/olney-tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-gray-400 hover:bg-[var(--primary)] hover:text-white transition-all"
                  >
                    <FaLinkedin size={20} />
                  </a>
                  <a
                    href="https://wa.me/33689177385"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-gray-400 hover:bg-[#25D366] hover:text-white transition-all"
                  >
                    <FaWhatsapp size={20} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-3"
            >
              <div className="glass-panel rounded-3xl p-8 md:p-10 border-[var(--primary)]/20">
                <h2 className="text-2xl font-bold text-white mb-2 font-['Space_Grotesk']">
                  Demander un devis gratuit
                </h2>
                <p className="text-gray-400 mb-8">
                  Décrivez votre projet et nous vous répondrons sous 24h.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Nom et Email sur la même ligne */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>

                  {/* Type de projet */}
                  <div>
                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">
                      Type de projet *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all appearance-none"
                    >
                      {services.map((service) => (
                        <option key={service} value={service} className="bg-[#030014]">
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">
                      Décrivez votre projet *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all resize-none"
                      placeholder="Parlez-nous de votre projet, vos objectifs, vos contraintes..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full btn-magnetic py-4 rounded-xl font-bold text-lg ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    } bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white hover:shadow-purple-500/40 transition-all`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Envoi en cours...
                      </span>
                    ) : (
                      'Envoyer ma demande'
                    )}
                  </button>

                  {submitStatus.type === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-center"
                    >
                      {submitStatus.message}
                    </motion.div>
                  )}

                  {submitStatus.type === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-center"
                    >
                      {submitStatus.message}
                    </motion.div>
                  )}

                  <p className="text-gray-500 text-xs text-center">
                    En soumettant ce formulaire, vous acceptez notre{' '}
                    <a href="/confidentialite" className="text-[var(--primary)] underline">
                      politique de confidentialité
                    </a>
                    .
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Questions fréquentes
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "Quel est le délai de réponse ?",
                a: "Nous répondons à toutes les demandes sous 24h ouvrées. Pour les urgences, appelez-nous directement."
              },
              {
                q: "Le devis est-il gratuit ?",
                a: "Oui, tous nos devis sont gratuits et sans engagement. Nous prenons le temps de comprendre votre projet."
              },
              {
                q: "Intervenez-vous en dehors de Lille ?",
                a: "Oui ! Nous intervenons dans toute la métropole lilloise et les Hauts-de-France. Pour le reste de la France, nous travaillons à distance."
              },
              {
                q: "Comment se déroule un premier rendez-vous ?",
                a: "En visio ou par téléphone, nous discutons de vos besoins, objectifs et contraintes. Durée : environ 30 minutes."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-panel rounded-2xl p-6"
              >
                <h3 className="text-white font-bold mb-2">{item.q}</h3>
                <p className="text-gray-400">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
